import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  greyColours,
  brandColours,
  fontSize,
  standardTransition,
  fontWeights,
} from '../styles';
import { Container, Link, Svg } from './ui';
import MegaMenuBannersCta from './MegaMenuBannersCta';
import MegaMenuSpaCta from './MegaMenuSpaCta';
import locationPinIcon from '../images/location-pin-icon.svg';
import mapLocationPinIcon from '../images/map-location-pin-icon.svg';
import flowerIcon from '../images/flower-icon.svg';
import bedIcon from '../images/bed-icon.svg';
import facialTreatmentIcon from '../images/facial-treatment-icon.svg';
import voucherIcon from '../images/voucher-icon-v2.svg';
import arrowIcon from '../images/arrow-icon.bg.svg';
import { slugify } from '../utils';

const StyledMegaMenu = styled.div`
  ${minBreakpointQuery.mlarge`
    position: absolute;
    padding-top: 15px;
  `}
`;

const StyledContainer = styled(Container)`
  max-width: 1300px;
`;

const StyledOuter = styled.div`
  ${minBreakpointQuery.mlarge`
    color: ${brandColours.primary};
    background-color: ${standardColours.white};
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 4px 8px 0 ${standardColours.transparentBlack(0.1)};
  `}

  ${({ hasCallToActions }) => {
    if (hasCallToActions) {
      return css`
        ${minBreakpointQuery.mlarge`
          display: grid;
          grid-template-columns: 2.7fr 1fr;
        `}

        ${minBreakpointQuery.large`
          gap: 20px;
        `}

        ${minBreakpointQuery.xlarge`
          gap: 40px;
        `}

        ${minBreakpointQuery.xxlarge`
          gap: 80px;
        `}
      `;
    }
  }}
`;

const StyledInner = styled.div`
  ${minBreakpointQuery.mlarge`
    display: grid;
    padding: 30px 40px;
  `}
`;

const StyledViewAllLink = styled(Link)`
  ${maxBreakpointQuery.mlarge`
    ${fontSize(20)};
    padding: 6px 10px;
  `}

  ${maxBreakpointQuery.tsmall`
    ${fontSize(18)};
  `}

  ${minBreakpointQuery.mlarge`
    order: 2;
    display: block;
    padding: 16px 20px;
    background: url(${arrowIcon}) no-repeat calc(100% - 20px) 50% / 21px 14px
      ${brandColours.transparentPrimary(0.1)};
    border-radius: 10px;
  `}
`;

const StyledList = styled.ul`
  ${minBreakpointQuery.mlarge`
    display: grid;
    grid-template-columns: repeat(${({ itemsNum }) => itemsNum}, 1fr);
    gap: 40px;
  `}

  ${minBreakpointQuery.large`
    gap: 50px;
  `}
`;

const StyledItem = styled.li`
  > span {
    position: relative;

    ${maxBreakpointQuery.mlarge`
      ${fontSize(20)};
      display:block;
      width: 100%;
      padding: 6px 30px 6px 10px;
      cursor: pointer;
    `}

    ${maxBreakpointQuery.tsmall`
      ${fontSize(18)};
    `}

    ${minBreakpointQuery.mlarge`
      padding-left: 32px;
      ${fontSize(18)};
      font-weight: ${fontWeights.bold};
    `}

    svg {
      ${maxBreakpointQuery.mlarge`
        display: none;
      `}

      ${minBreakpointQuery.mlarge`
        position: absolute;
        top: 50%;
        left: 0;
        width: 20px;
        height: 20px;
        transform: translateY(-50%);
      `}
    }

    &:after {
      ${maxBreakpointQuery.mlarge`
      content: '';
      position: absolute;
      top: 50%;
      right: 0;
      transform: translateY(-50%) rotate(45deg);
      border: solid ${brandColours.primary};
      border-width: 0 2px 2px 0;
      height: 8px;
      width: 8px;
    `}
    }

    ${({ isMegaMenuItemActive }) => {
      if (isMegaMenuItemActive) {
        return css`
          &:after {
            ${maxBreakpointQuery.mlarge`
              transform: translateY(-50%) rotate(-135deg);
            `}
          }
        `;
      }
    }}
  }
`;

const StyledSubList = styled.ul`
  ${maxBreakpointQuery.mlarge`
    margin: 4px 0 4px 10px;
  `}

  ${minBreakpointQuery.mlarge`
    margin-top: 20px;
    padding: 20px 0;
    column-count: ${({ itemsNum }) => (itemsNum === 1 ? 3 : 2)};
    column-gap: 20px;
    border-top: 1px solid ${greyColours.lightGrey};
  `}

  ${({ isMegaMenuItemActive }) => {
    if (!isMegaMenuItemActive) {
      return css`
        ${maxBreakpointQuery.mlarge`
          display: none;
        `}
      `;
    }
  }}
`;

const StyledSubItem = styled.li`
  ${minBreakpointQuery.mlarge`
    margin-top: 3px;
    margin-bottom: 3px;
  `}

  &:first-child {
    ${maxBreakpointQuery.mlarge`
      margin-top: 0;
    `}
  }
`;

const StyledSubLink = styled(Link)`
  display: block;
  line-height: 1.2;

  ${maxBreakpointQuery.mlarge`
    padding: 6px 10px;
    ${fontSize(20)};
  `}

  ${maxBreakpointQuery.tsmall`
    ${fontSize(18)};
  `}

  ${minBreakpointQuery.mlarge`
    position: relative;
    padding: 8px 36px 8px 10px;
    border-radius: 10px;
    transition: ${standardTransition('background-color')};
  `}

  &:before {
    ${minBreakpointQuery.mlarge`
      content: '';
      position: absolute;
      top: 50%;
      right: 10px;
      height: 11px;
      width: 17px;
      background: url(${arrowIcon}) no-repeat;
      opacity: 0;
      transform: translateY(-50%);
      transition: ${standardTransition('opacity')};
    `}
  }

  &:hover,
  &.current-page {
    ${minBreakpointQuery.mlarge`
      background-color: ${brandColours.senary};
    `}

    &:before {
      ${minBreakpointQuery.mlarge`
        opacity: 1;
      `}
    }
  }
`;

const Item = ({ text, subItems, itemsNum, parentText, setNavActive }) => {
  const [isMegaMenuItemActive, setMegaMenuItemActive] = useState(false);

  const getIconByText = (text, parentText) => {
    const t = slugify(text);
    const p = slugify(parentText || '');

    if (t === 'popular-packages' && p === 'spa-days') {
      return flowerIcon;
    } else if (t === 'popular-packages' && p === 'spa-breaks') {
      return facialTreatmentIcon;
    } else if (t === 'flexible-vouchers' || t === 'voucher-packages') {
      return voucherIcon;
    } else if (t === 'spas-by-region') {
      return mapLocationPinIcon;
    } else if (t === 'best-sellers' || t === 'popular-spa-groups') {
      return bedIcon;
    } else {
      return locationPinIcon;
    }
  };

  const icon = getIconByText(text, parentText);

  return (
    <StyledItem isMegaMenuItemActive={isMegaMenuItemActive}>
      <span onClick={() => setMegaMenuItemActive(!isMegaMenuItemActive)}>
        <Svg image={icon} />
        {text}
      </span>
      {subItems && subItems.length > 0 && (
        <StyledSubList
          itemsNum={itemsNum}
          isMegaMenuItemActive={isMegaMenuItemActive}
        >
          {subItems.map(({ text, url }, i) => (
            <StyledSubItem key={`sub-megamenu-item-${i}`}>
              <StyledSubLink
                to={url}
                onClick={() => {
                  if (url === window.location.pathname) {
                    setNavActive(false);
                  }
                }}
                activeClassName="current-page"
              >
                <span>{text}</span>
              </StyledSubLink>
            </StyledSubItem>
          ))}
        </StyledSubList>
      )}
    </StyledItem>
  );
};

const MegaMenu = ({
  items,
  viewAllLink,
  callToActions,
  setNavActive,
  ...props
}) =>
  items &&
  items.length > 0 && (
    <StyledMegaMenu {...props}>
      <StyledContainer>
        <StyledOuter hasCallToActions={callToActions}>
          <StyledInner>
            {viewAllLink && (
              <StyledViewAllLink
                to={viewAllLink.url}
                onClick={() => {
                  if (viewAllLink.url === window.location.pathname) {
                    setNavActive(false);
                  }
                }}
              >
                View all {viewAllLink.text}
              </StyledViewAllLink>
            )}
            <StyledList itemsNum={items.length}>
              {items.map((item, id) => (
                <Item
                  key={id}
                  {...item}
                  itemsNum={items.length}
                  parentText={viewAllLink.text}
                  setNavActive={setNavActive}
                />
              ))}
            </StyledList>
          </StyledInner>
          {callToActions && callToActions.banners ? (
            <MegaMenuBannersCta {...callToActions.banners} />
          ) : callToActions && callToActions.spaSpotlight ? (
            <MegaMenuSpaCta {...callToActions.spaSpotlight.spa} />
          ) : (
            ''
          )}
        </StyledOuter>
      </StyledContainer>
    </StyledMegaMenu>
  );

export default MegaMenu;
